import api from "./api"

/**
 * Serviço completo para gerenciamento de Ordens de Serviço
 * Baseado na API Syskeeper
 */

// ============================================
// OS (Ordem de Serviço)
// ============================================

/**
 * Lista todas as OS com filtros opcionais
 * @param {Object} filtros - Filtros opcionais
 * @param {number} filtros.oid_empresa - ID da empresa
 * @param {string} filtros.status - Status da OS
 * @param {string} filtros.protocolo - Número do protocolo
 * @param {number} filtros.tipo_ordem - Tipo da ordem
 * @param {string} filtros.ds_ordem - Descrição da ordem
 * @param {string} filtros.ds_item - Descrição do item
 * @param {string} filtros.responsavel - Nome do responsável
 */
export const listarOS = async (filtros = {}) => {
  const params = new URLSearchParams()

  if (filtros.oid_empresa) params.append("oid_empresa", filtros.oid_empresa)
  if (filtros.status) params.append("status", filtros.status)
  if (filtros.protocolo) params.append("protocolo", filtros.protocolo)
  if (filtros.tipo_ordem) params.append("tipo_ordem", filtros.tipo_ordem)
  if (filtros.ds_ordem) params.append("ds_ordem", filtros.ds_ordem)
  if (filtros.ds_item) params.append("ds_item", filtros.ds_item)
  if (filtros.responsavel) params.append("responsavel", filtros.responsavel)

  const queryString = params.toString()
  const endpoint = queryString ? `/os?${queryString}` : "/os"

  const response = await api.get(endpoint)
  return response.data
}

/**
 * Busca uma OS pelo ID
 * @param {number} oidOS - ID da OS
 */
export const buscarOSPorId = async oidOS => {
  const response = await api.get(`/os/${oidOS}`)
  // A API retorna um array, então pegamos o primeiro item
  const data = response.data
  return Array.isArray(data) ? data[0] : data
}

/**
 * Busca todas as OS de uma empresa
 * @param {number} oidEmpresa - ID da empresa
 */
export const buscarOSPorEmpresa = async oidEmpresa => {
  const response = await api.get(`/os/empresa/${oidEmpresa}`)
  return response.data
}

/**
 * Cria uma nova OS
 * @param {Object} dados - Dados da OS
 * @param {number} dados.OID_EMPRESA - ID da empresa
 * @param {number} dados.TIPO_ORDEM - Tipo da ordem (ID do GL_TIPO)
 * @param {string} dados.DS_ORDEM - Descrição da ordem
 * @param {string} dados.DS_ITEM - Descrição do item
 * @param {string} dados.RESPONSAVEL - Nome do responsável
 * @param {string} dados.STATUS - Status (OPEN, IN_PROGRESS, etc)
 * @param {string} dados.PROTOCOLO - Número do protocolo
 */
export const criarOS = async dados => {
  const response = await api.post("/os", dados)
  return response.data
}

/**
 * Atualiza uma OS existente
 * @param {number} oidOS - ID da OS
 * @param {Object} dados - Dados para atualização
 */
export const atualizarOS = async (oidOS, dados) => {
  const response = await api.put(`/os/${oidOS}`, dados)
  return response.data
}

/**
 * Exclui uma OS
 * @param {number} oidOS - ID da OS
 */
export const excluirOS = async oidOS => {
  const response = await api.delete(`/os/${oidOS}`)
  return response.data
}

// ============================================
// OS ANEXO
// ============================================

/**
 * Lista todos os anexos
 */
export const listarAnexos = async () => {
  const response = await api.get("/os_anexo")
  return response.data
}

/**
 * Busca um anexo pelo ID
 * @param {number} oidAnexo - ID do anexo
 */
export const buscarAnexoPorId = async oidAnexo => {
  const response = await api.get(`/os_anexo/${oidAnexo}`)
  return response.data
}

/**
 * Busca todos os anexos de uma OS
 * @param {number} oidOS - ID da OS
 */
export const buscarAnexosPorOS = async oidOS => {
  const response = await api.get(`/os_anexo/os/${oidOS}`)
  return response.data
}

/**
 * Cria um novo anexo (com upload de arquivo)
 * @param {Object} dados - Dados do anexo
 * @param {File} dados.file - Arquivo a ser enviado
 * @param {number} dados.OID_OS - ID da OS relacionada
 * @param {string} dados.DS_ANEXO - Descrição do anexo
 * @param {string} dados.RESPONSAVEL - Nome do responsável
 * @param {string} dados.NOME_RECEBIMENTO - Nome de quem recebeu
 * @param {string} dados.EMAIL_RECEBIMENTO - Email de quem recebeu
 * @param {string} dados.DS_ITEM - Descrição do item
 * @param {number} dados.QTD_ITEM - Quantidade do item
 */
export const criarAnexo = async dados => {
  const formData = new FormData()

  if (dados.file) formData.append("file", dados.file)
  if (dados.OID_OS) formData.append("OID_OS", dados.OID_OS)
  if (dados.DS_ANEXO) formData.append("DS_ANEXO", dados.DS_ANEXO)
  if (dados.RESPONSAVEL) formData.append("RESPONSAVEL", dados.RESPONSAVEL)
  if (dados.NOME_RECEBIMENTO)
    formData.append("NOME_RECEBIMENTO", dados.NOME_RECEBIMENTO)
  if (dados.EMAIL_RECEBIMENTO)
    formData.append("EMAIL_RECEBIMENTO", dados.EMAIL_RECEBIMENTO)
  if (dados.DS_ITEM) formData.append("DS_ITEM", dados.DS_ITEM)
  if (dados.QTD_ITEM) formData.append("QTD_ITEM", dados.QTD_ITEM)

  const response = await api.post("/os_anexo", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
  return response.data
}

/**
 * Atualiza um anexo existente (com upload de arquivo opcional)
 * @param {number} oidAnexo - ID do anexo
 * @param {Object} dados - Dados para atualização
 */
export const atualizarAnexo = async (oidAnexo, dados) => {
  const formData = new FormData()

  if (dados.file) formData.append("file", dados.file)
  if (dados.OID_OS) formData.append("OID_OS", dados.OID_OS)
  if (dados.DS_ANEXO) formData.append("DS_ANEXO", dados.DS_ANEXO)
  if (dados.RESPONSAVEL) formData.append("RESPONSAVEL", dados.RESPONSAVEL)
  if (dados.NOME_RECEBIMENTO)
    formData.append("NOME_RECEBIMENTO", dados.NOME_RECEBIMENTO)
  if (dados.EMAIL_RECEBIMENTO)
    formData.append("EMAIL_RECEBIMENTO", dados.EMAIL_RECEBIMENTO)
  if (dados.DS_ITEM) formData.append("DS_ITEM", dados.DS_ITEM)
  if (dados.QTD_ITEM) formData.append("QTD_ITEM", dados.QTD_ITEM)

  const response = await api.put(`/os_anexo/${oidAnexo}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
  return response.data
}

/**
 * Dispara email relacionado a um anexo
 * @param {number} oidAnexo - ID do anexo
 */
export const dispararEmailAnexo = async oidAnexo => {
  const response = await api.post(`/os_anexo/mail/${oidAnexo}`)
  return response.data
}

/**
 * Exclui um anexo
 * @param {number} oidAnexo - ID do anexo
 */
export const excluirAnexo = async oidAnexo => {
  const response = await api.delete(`/os_anexo/${oidAnexo}`)
  return response.data
}

// ============================================
// GL TIPO (Tipos de Ordem de Serviço)
// ============================================

/**
 * Lista todos os tipos de serviço
 */
export const listarTipos = async () => {
  const response = await api.get("/gl_tipo")
  return response.data
}

/**
 * Busca um tipo pelo ID
 * @param {number} oidTipo - ID do tipo
 */
export const buscarTipoPorId = async oidTipo => {
  const response = await api.get(`/gl_tipo/${oidTipo}`)
  return response.data
}

/**
 * Cria um novo tipo de serviço
 * @param {Object} dados - Dados do tipo
 * @param {string} dados.NAME - Nome do tipo
 */
export const criarTipo = async dados => {
  const response = await api.post("/gl_tipo", dados)
  return response.data
}

/**
 * Atualiza um tipo existente
 * @param {number} oidTipo - ID do tipo
 * @param {Object} dados - Dados para atualização
 * @param {string} dados.NAME - Nome do tipo
 */
export const atualizarTipo = async (oidTipo, dados) => {
  const response = await api.put(`/gl_tipo/${oidTipo}`, dados)
  return response.data
}

/**
 * Exclui um tipo
 * @param {number} oidTipo - ID do tipo
 */
export const excluirTipo = async oidTipo => {
  const response = await api.delete(`/gl_tipo/${oidTipo}`)
  return response.data
}

// ============================================
// EXPORTS DEFAULT
// ============================================

const OrdemServicoService = {
  // OS
  listarOS,
  buscarOSPorId,
  buscarOSPorEmpresa,
  criarOS,
  atualizarOS,
  excluirOS,

  // Anexos
  listarAnexos,
  buscarAnexoPorId,
  buscarAnexosPorOS,
  criarAnexo,
  atualizarAnexo,
  dispararEmailAnexo,
  excluirAnexo,

  // Tipos
  listarTipos,
  buscarTipoPorId,
  criarTipo,
  atualizarTipo,
  excluirTipo,
}

export default OrdemServicoService
